import { Component, inject, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Usuario } from '../../../../domain/usuario.domain';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { SecurityService } from '../../../../services/security/security.service';
import { UsuariosService } from '../../../../services/usuario/usuarios.service';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';

@Component({
  selector: 'app-user-info-card',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ToastModule,
  ],
  providers: [MessageService],
  templateUrl: './user-info-card.component.html',
  styles: ``
})
export class UserInfoCardComponent implements OnInit {
  @Input() usuario: Usuario = {};
  
  private fb = inject(FormBuilder);
  private securityService = inject(SecurityService);
  private usuariosService = inject(UsuariosService);
  private messageService = inject(MessageService);

  isEditing = false;
  isSaving = false;
  form!: FormGroup;

  ngOnInit() {
    console.log('UserInfoCard - Usuario recibido:', this.usuario);
    this.initForm();
  }

  initForm() {
    this.form = this.fb.group({
      name: [{ value: this.usuario.name || '', disabled: !this.isEditing }, [Validators.required]],
      lastName: [{ value: this.usuario.lastName || '', disabled: !this.isEditing }, [Validators.required]],
      email: [{ value: this.usuario.email || '', disabled: !this.isEditing }, [Validators.required, Validators.email]],
      phone: [{ value: this.usuario.phone || '', disabled: !this.isEditing }]
    });
  }

  toggleEdit() {
    this.isEditing = !this.isEditing;
    
    if (this.isEditing) {
      // Habilitar todos los campos
      Object.keys(this.form.controls).forEach(key => {
        this.form.controls[key].enable();
      });
    } else {
      // Restaurar valores originales y deshabilitar
      this.form.patchValue({
        name: this.usuario.name || '',
        lastName: this.usuario.lastName || '',
        email: this.usuario.email || '',
        phone: this.usuario.phone || ''
      });
      Object.keys(this.form.controls).forEach(key => {
        this.form.controls[key].disable();
      });
    }
  }

  handleSave() {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atención',
        detail: 'Por favor complete todos los campos requeridos'
      });
      // Marcar todos los campos como tocados para mostrar errores
      Object.keys(this.form.controls).forEach(key => {
        this.form.controls[key].markAsTouched();
      });
      return;
    }

    if (!this.usuario.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo identificar al usuario'
      });
      return;
    }

    this.isSaving = true;
    
    // Crear objeto con SOLO los campos que el backend acepta
    const updatedUser: Usuario = {
      id: this.usuario.id,
      name: this.form.get('name')?.value,
      lastName: this.form.get('lastName')?.value,
      email: this.form.get('email')?.value,
      phone: this.form.get('phone')?.value || null
    };

    this.usuariosService.guardarUsuario(updatedUser as any).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: 'Información actualizada correctamente'
        });
        // Actualizar el usuario local con la respuesta del servicio
        // El servicio ya maneja el null de API Platform 4 y devuelve el usuario actualizado
        if (response) {
          this.usuario = { ...this.usuario, ...response };
        }
        // Sincronizar el usuario logueado (localStorage/subject) con el backend
        this.securityService.getUserMe().subscribe();
        this.isSaving = false;
        this.isEditing = false;
        
        // Deshabilitar campos después de guardar
        Object.keys(this.form.controls).forEach(key => {
          this.form.controls[key].disable();
        });
      },
      error: (error) => {
        console.error('Error al actualizar usuario:', error);
        const errorMsg = error?.error?.['hydra:description'] || error?.error?.message || 'No se pudo actualizar la información';
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: errorMsg
        });
        this.isSaving = false;
      }
    });
  }

  getFieldError(field: string): string | null {
    const control = this.form.get(field);
    if (control && control.invalid && (control.dirty || control.touched)) {
      if (control.hasError('required')) {
        return 'Este campo es requerido';
      }
      if (control.hasError('email')) {
        return 'Ingrese un email válido';
      }
    }
    return null;
  }
}
